import React, { useState } from 'react';
import { RefreshCw, Brain, FileText, AlertCircle, Plus } from 'lucide-react';
import ReportSelector from './ReportSelector';
import MarkdownRenderer from './MarkdownRenderer';
import { AIReportsTabProps } from '../../types/components';
import { DiagnosisReport } from '../../types/problemDiagnosis';

const AIReportsTab: React.FC<AIReportsTabProps> = ({
  reports,
  selectedReportId,
  onReportSelect,
  onDeleteReport,
  onRegenerateReport,
  isRegenerating,
  hasAnswers
}) => {
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');

  const selectedReport = reports.find(report => report.id === selectedReportId);

  const handleReportSelect = (reportId: string) => {
    onReportSelect(reportId);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
  };

  const renderReportContent = (report: DiagnosisReport) => {
    return (
      <div className="space-y-8">
        {/* 报告标题和元信息 */}
        <div className="border-b border-gray-200 pb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {report.title}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>生成时间：{new Date(report.created_at).toLocaleString('zh-CN')}</span>
                {report.confidence_score && (
                  <span>
                    置信度：{Math.round(report.confidence_score * 100)}%
                  </span>
                )}
                <span>版本：v{report.version}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {report.is_latest && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  最新版本
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 核心结论 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
            <Brain className="w-5 h-5 mr-2" />
            核心结论
          </h2>
          <div className="text-blue-800">
            <MarkdownRenderer 
              content={report.core_conclusion} 
              className="prose-blue"
            />
          </div>
        </div>

        {/* 分系统发现 */}
        {report.system_findings && report.system_findings.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              分系统发现
            </h2>
            <div className="grid gap-4">
              {report.system_findings.map((finding, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-medium text-purple-600 mb-2">
                    {finding.system}
                  </h3>
                  <MarkdownRenderer 
                    content={finding.finding} 
                    className="text-gray-700"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 下一步建议 */}
        {report.next_steps && report.next_steps.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              下一步行动建议
            </h2>
            <div className="space-y-4">
              {report.next_steps.map((step, index) => (
                <div key={index} className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                  <h3 className="font-medium text-green-700 mb-2">
                    {step.level}
                  </h3>
                  <MarkdownRenderer 
                    content={step.description} 
                    className="text-green-800"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI详细分析 */}
        {report.ai_analysis && (
          <div className="border-t border-gray-200 pt-8">
            <details className="group">
              <summary className="cursor-pointer list-none">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Brain className="w-5 h-5 mr-2" />
                    AI 详细分析数据
                  </h2>
                  <div className="text-sm text-gray-500 group-open:rotate-180 transition-transform">
                    ▼
                  </div>
                </div>
              </summary>
              
              <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="text-sm text-gray-600 mb-2">AI原始分析结果：</div>
                <div className="bg-white rounded border p-3 max-h-96 overflow-y-auto">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                    {typeof report.ai_analysis === 'string'
                      ? report.ai_analysis
                      : JSON.stringify(report.ai_analysis, null, 2)
                    }
                  </pre>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>
    );
  };

  // 空状态 - 没有答案
  if (!hasAnswers) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            无法生成AI诊断报告
          </h3>
          <p className="text-gray-600 mb-6">
            请先完成问题描述和五大原理分析，然后才能生成AI诊断报告。
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 建议至少完成3个原理的分析，以确保AI能够生成高质量的诊断报告。
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 空状态 - 有答案但没有报告
  if (reports.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            暂无AI诊断报告
          </h3>
          <p className="text-gray-600 mb-6">
            您已完成问题分析，现在可以生成AI诊断报告了。
          </p>
          
          <button
            onClick={onRegenerateReport}
            disabled={isRegenerating}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 mx-auto"
          >
            {isRegenerating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>正在生成报告...</span>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span>生成AI诊断报告</span>
              </>
            )}
          </button>
          
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>提示：</strong>
              AI将基于您的问题描述和五大原理分析生成专业的诊断报告，
              包括核心结论、分系统发现和具体的行动建议。
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 移动端或小屏幕：显示报告列表或详情
  if (viewMode === 'list' || !selectedReport) {
    return (
      <div className="space-y-6">
        {/* 页面头部 */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Brain className="w-6 h-6 mr-2 text-purple-600" />
              AI诊断报告
            </h2>
            <p className="text-gray-600 mt-1">
              基于系统思维生成的专业诊断分析
            </p>
          </div>
          
          <button
            onClick={onRegenerateReport}
            disabled={isRegenerating}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isRegenerating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>生成中...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>重新生成</span>
              </>
            )}
          </button>
        </div>

        {/* 报告选择器 */}
        <ReportSelector
          reports={reports.map(report => ({
            id: report.id,
            title: report.title,
            created_at: report.created_at,
            confidence_score: report.confidence_score,
            version: report.version,
            is_latest: report.is_latest,
            generation_trigger: report.generation_trigger,
            summary: report.core_conclusion.substring(0, 200) + '...'
          }))}
          selectedReportId={selectedReportId}
          onReportSelect={handleReportSelect}
          onDeleteReport={onDeleteReport}
        />
      </div>
    );
  }

  // 桌面端：显示选中的报告详情
  return (
    <div className="space-y-6">
      {/* 返回按钮 (移动端) */}
      <div className="md:hidden">
        <button
          onClick={handleBackToList}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          ← 返回报告列表
        </button>
      </div>

      {/* 报告内容 */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6">
          {selectedReport ? (
            renderReportContent(selectedReport)
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">请选择一个报告查看详细内容</p>
            </div>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      {selectedReport && (
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600">
            当前查看：{selectedReport.title}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onRegenerateReport}
              disabled={isRegenerating}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isRegenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>生成中...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>基于当前内容重新生成</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIReportsTab;